import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import SiteCTA from '@/components/SiteCTA';
import Flywheel from '@/components/Flywheel';
import { Icon } from '@iconify/react';
import { 
  ArrowRight, 
  Clock, 
  Zap, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  Activity,
  Target,
  Sparkles,
  Timer,
  Rocket,
  ChevronRight,
  ChevronDown,
  Calendar,
  Users,
  BarChart3,
  Building2,
  Layers,
  GitBranch,
  Settings,
  Shield,
  Quote
} from 'lucide-react';

// Animated background component
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 left-20 w-72 h-72 bg-gold/20 rounded-full blur-3xl"
        animate={{
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 right-20 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 0],
          y: [0, 50, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

// Service card component
const TransformationCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.15 }}
      whileHover={{ scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative group perspective-1000"
    >
      <div className="relative h-full">
        {/* Glow effect */}
        <motion.div
          className="absolute -inset-0.5 bg-gradient-to-r from-gold via-amber-400 to-gold rounded-2xl opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500"
          animate={isHovered ? { opacity: 0.7 } : { opacity: 0 }}
        />
        
        {/* Main card */}
        <div className="relative h-full bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl border border-gold/20 overflow-hidden backdrop-blur-xl">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]" />
          </div>
          
          {/* Content */}
          <div className="relative p-8 h-full flex flex-col">
            {/* Header with icon */}
            <div className="flex items-start justify-between mb-6">
              <motion.div
                className="w-16 h-16 bg-gradient-to-br from-gold to-amber-400 rounded-2xl flex items-center justify-center shadow-2xl"
                animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}}
                transition={{ duration: 0.5 }}
              >
                <service.icon className="w-8 h-8 text-black" />
              </motion.div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
              {service.title}
            </h3>

            {/* Description */}
            <p className="text-gray-300 text-base leading-relaxed mb-6 flex-grow">
              {service.description}
            </p>

            {/* Key Features */}
            <div className="space-y-2 mb-6">
              {service.features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: index * 0.1 + idx * 0.05 }}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                  <span className="text-sm text-gray-400">{feature}</span>
                </motion.div>
              ))}
            </div>

            {/* Footer with timeline */}
            <motion.div 
              className="mt-auto pt-6 border-t border-gold/20 flex items-center justify-between"
              initial={{ opacity: 0.7 }}
              animate={isHovered ? { opacity: 1 } : { opacity: 0.7 }}
            >
              <div className="flex items-center gap-3">
                <Timer className="w-5 h-5 text-gold" />
                <span className="text-white font-medium">{service.duration}</span>
              </div>
            </motion.div>
          </div>

          {/* Animated corner accent */}
          <motion.div
            className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gold/20 to-transparent"
            animate={isHovered ? { scale: 1.2 } : { scale: 1 }}
            style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
          />
        </div>
      </div>
    </motion.div>
  );
};

// Value proposition component
const ValueProp = ({ icon: Icon, title, description, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay }}
      className="text-center"
    >
      <motion.div
        whileHover={{ rotate: 360 }}
        transition={{ duration: 0.5 }}
        className="w-20 h-20 bg-gradient-to-br from-gold to-amber-400 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl"
      >
        <Icon className="w-10 h-10 text-black" />
      </motion.div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm">{description}</p>
    </motion.div>
  );
};

const PMOTransformationPage = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  
  const transformationServices = [
    {
      title: 'PMO Strategy & Design',
      icon: Building2,
      description: 'Build your PMO from the ground up or redesign existing structures for maximum impact.',
      features: [
        'PMO charter development',
        'Operating model design',
        'Governance framework creation',
        'Tool and technology selection'
      ],
      duration: '8-12 weeks'
    },
    {
      title: 'Process & Methodology',
      icon: GitBranch,
      description: 'Implement world-class project management methodologies tailored to your culture.',
      features: [
        'Agile/Waterfall/Hybrid implementation',
        'Process standardization',
        'Template and toolkit development',
        'Best practice integration'
      ],
      duration: '6-10 weeks'
    },
    {
      title: 'Portfolio Management',
      icon: Layers,
      description: 'Align your project portfolio with strategic objectives and optimize resource allocation.',
      features: [
        'Portfolio prioritization frameworks',
        'Resource capacity planning',
        'Investment optimization',
        'Benefits realization tracking'
      ],
      duration: '8-12 weeks'
    },
    {
      title: 'PMO Maturity Assessment',
      icon: BarChart3,
      description: 'Evaluate current state and create a roadmap for continuous PMO improvement.',
      features: [
        'Comprehensive maturity evaluation',
        'Gap analysis and benchmarking',
        'Improvement roadmap',
        'Change management planning'
      ],
      duration: '4-6 weeks'
    },
    {
      title: 'Governance & Reporting',
      icon: Shield,
      description: 'Establish clear decision rights and create executive-ready reporting systems.',
      features: [
        'Steering committee structure',
        'Executive dashboards',
        'KPI framework development',
        'Risk management processes'
      ],
      duration: '6-8 weeks'
    },
    {
      title: 'Change Management',
      icon: Settings,
      description: 'Ensure successful adoption of new PMO practices across your organization.',
      features: [
        'Stakeholder engagement plans',
        'Training program development',
        'Communication strategies',
        'Adoption monitoring'
      ],
      duration: '4-8 weeks'
    }
  ];

  const valueProps = [
    { icon: Target, title: 'Strategic Alignment', description: 'Align the PMO & the Business Strategy' },
    { icon: TrendingUp, title: 'Iterative Improvement', description: 'Phased Change. Clear Metrics.' },
    { icon: Rocket, title: 'Sustained Value', description: 'Adaptive PMO Structure. Long-Term Approach.' },
    { icon: Users, title: 'Co - Created Roadmap', description: 'Define vision. Close gaps. Build your roadmap.' },
  ];

  return (
    <>
      <div className="min-h-screen bg-black text-white overflow-hidden">
        {/* Hero Section with Parallax */}
        <section className="relative min-h-screen flex items-center justify-center">
          <AnimatedBackground />
          
          <motion.div 
            style={{ y }}
            className="container mx-auto px-4 relative z-10 py-32"
          >
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-center max-w-5xl mx-auto"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-8 border border-gold/30 backdrop-blur-sm"
              >
                <Sparkles className="w-5 h-5 text-gold" />
                <span className="text-gold font-bold text-sm uppercase tracking-wider">PMO Excellence</span>
                <Sparkles className="w-5 h-5 text-gold" />
              </motion.div>
              
              {/* Main heading with gradient */}
              <motion.h1 
                className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold">
                  PMO Transformation
                </span>
                <br />
                <span className="text-white text-4xl md:text-5xl lg:text-6xl">
                  Build. Elevate. Optimize.
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-12 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Transform your PMO into a strategic powerhouse that delivers measurable business value.
              </motion.p>

            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 inset-x-0 flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
            <div className="flex flex-col items-center gap-2 text-gold">
              <span className="text-sm uppercase tracking-wider">Scroll to explore</span>
              <ChevronDown className="w-6 h-6" />
            </div>
          </motion.div>
        </section>

        

        {/* Value Props Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {valueProps.map((prop, index) => (
                <ValueProp key={prop.title} {...prop} delay={index * 0.1} />
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid Section removed per request */}

        

        {/* Process Section */}
        <section className="py-20 relative bg-gradient-to-b from-black via-gray-900 to-black">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  The Transformation Journey
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Five phases guided by business alignment, agility, and value creation.
              </p>
            </motion.div>

            {/* Journey Steps */}
            <div className="max-w-4xl mx-auto">
              {[
                { phase: 'Discover', title: 'Assess maturity and align to the OptiGold model.', desc: '' },
                { phase: 'Design', title: 'Define PMO strategy, governance, and mandate to identify capability gaps.', desc: '' },
                { phase: 'Implement', title: 'Develop and execute a practical roadmap.', desc: '' },
                { phase: 'Enhance', title: 'Monitor outcomes and refine the approach.', desc: '' },
                { phase: 'Deliver', title: 'Realize value and scale what works.', desc: '' },
              ].map((step, index) => (
                <motion.div
                  key={step.phase}
                  className="flex items-center gap-8 mb-8"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-gold to-amber-400 rounded-full flex items-center justify-center">
                      <span className="text-black font-bold text-2xl">{index + 1}</span>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="bg-gradient-to-r from-gray-900 to-transparent p-6 rounded-2xl border-l-4 border-gold">
                      <div className="text-gold font-bold mb-2">{step.phase}</div>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      {step.desc && <p className="text-gray-400">{step.desc}</p>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  Typical Outcomes
                </span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { metric: '2x', label: 'ROI in Year 1', icon: 'solar:dollar-minimalistic-bold' },
                { metric: '4%', label: 'Less Portfolio Waste', icon: 'solar:chart-2-bold' },
                { metric: '40%', label: 'Portfolio Risk Reduction', icon: 'solar:shield-keyhole-bold' },
                { metric: '50%', label: 'Tighter Project Control', icon: 'solar:slider-vertical-bold' },
                { metric: '30%', label: 'Improved Project ROI', icon: 'solar:graph-up-bold' },
                { metric: '2x', label: 'Improved Portfolio Alignment', icon: 'solar:target-bold' },
              ].map((result, index) => (
                <motion.div
                  key={result.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-900/80 to-black/80 p-6 rounded-2xl border border-gold/20 text-center hover:border-gold/40 transition-all"
                >
                  <Icon icon={result.icon} className="w-12 h-12 text-gold mx-auto mb-4" />
                  <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400 mb-2">
                    {result.metric}
                  </div>
                  <p className="text-gray-300">{result.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Opportunities to Unlock Value (match Quick Wins styling) */}
        <section className="py-20 relative bg-gradient-to-b from-black via-gray-900 to-black overflow-hidden">
          <motion.div
            className="absolute -top-10 -left-10 w-80 h-80 bg-gold/10 rounded-full blur-3xl"
            animate={{ x: [0, 30, 0], y: [0, 20, 0], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-10 -right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"
            animate={{ x: [0, -30, 0], y: [0, -20, 0], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  Opportunities to Unlock Value
                </span>
              </h2>
            </motion.div>

            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                "We're launching a PMO but want to make sure it's built right from day one.",
                "We have a PMO, but it's tactical. We need it to operate more strategically.",
                "Our PMO lacks consistency and executive confidence. We need structure, clarity, and measurable impact.",
                "We want to evolve our PMO to better support enterprise change and growth.",
                "We've outgrown our current PMO model and need to scale with more rigor, tools, and governance.",
                "We've invested in a PMO, but we're unclear if it's delivering measurable value to the business."
              ].map((text, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  className="relative group"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-br from-gold/0 via-gold/20 to-amber-400/0 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl border border-gold/20 rounded-2xl p-6 h-full">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold to-amber-400 flex items-center justify-center flex-shrink-0">
                        <Quote className="w-5 h-5 text-black" />
                      </div>
                      <p className="text-gray-200 leading-relaxed">{text}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom CTA section removed; using SiteCTA below */}
      </div>

      <SiteCTA />
    </>
  );
};

export default PMOTransformationPage;
